import { get, set, unset } from 'lodash';

import type { ContainerVolumeStatus } from '@/delivery-type-plugin/capabilities/container-storage';
import type { MaasWorkload } from '@/delivery-type-plugin/capabilities/maas-init/maasTypes';
import { safeParseJSON } from '@/utils/json-parse';

import type { MaasInferenceSpec } from './modelInstanceTypes';
import { parseStorageQuantityToGi } from './quantity';

/** 存储类展示信息（alias / name / provisioner） */
export interface StorageClassDisplayInfo {
  alias?: string;
  name?: string;
  provisioner?: string;
}

interface StorageClassNameOriginal {
  label?: string;
  value?: string;
  provisioner?: string;
}

function parseStorageClassNameOriginal(original: unknown): StorageClassNameOriginal | undefined {
  if (original == null || original === '') return undefined;
  if (typeof original === 'string') {
    return safeParseJSON(original, undefined) as StorageClassNameOriginal | undefined;
  }
  return original as StorageClassNameOriginal;
}

function normalizeStorageClassDisplayInfo(raw?: {
  alias?: string;
  name?: string;
  provisioner?: string;
}): StorageClassDisplayInfo | undefined {
  if (!raw) return undefined;
  const alias = raw.alias ? String(raw.alias).trim() : '';
  const name = raw.name ? String(raw.name).trim() : '';
  const provisioner = raw.provisioner ? String(raw.provisioner).trim() : '';
  if (!alias && !name && !provisioner) return undefined;
  return {
    ...(alias ? { alias } : {}),
    ...(name ? { name } : {}),
    ...(provisioner ? { provisioner } : {}),
  };
}

/** API storageClass → 展示结构 */
export function mapApiStorageClassToDisplayInfo(storageClass?: ContainerVolumeStatus['storageClass']): StorageClassDisplayInfo | undefined {
  if (!storageClass) return undefined;
  return normalizeStorageClassDisplayInfo({
    alias: storageClass.alias,
    name: storageClass.name,
    provisioner: storageClass.provisioner,
  });
}

/** 表单卷 storageClassNameOriginal → 展示结构（创建/编辑会话） */
export function getVolumeStorageClassInfo(volume?: Record<string, unknown>): StorageClassDisplayInfo | undefined {
  if (!volume) return undefined;
  const parsed = parseStorageClassNameOriginal(volume.storageClassNameOriginal);
  if (parsed) {
    const fromOriginal = normalizeStorageClassDisplayInfo({
      alias: parsed.label,
      name: parsed.value,
      provisioner: parsed.provisioner,
    });
    if (fromOriginal) return fromOriginal;
  }
  const template = getVolumePvcTemplate(volume);
  const templateName = get(template, 'storageClassName');
  if (templateName) {
    return normalizeStorageClassDisplayInfo({ name: String(templateName) });
  }
  return undefined;
}

/** 展示结构 → 表单 transient storageClassNameOriginal */
export function buildStorageClassNameOriginalFromInfo(info: StorageClassDisplayInfo): StorageClassNameOriginal {
  return {
    label: info.alias || info.name || '',
    value: info.name || info.alias || '',
    ...(info.provisioner ? { provisioner: info.provisioner } : {}),
  };
}

function findVolumeStatusEntry(
  volumeStatus: ContainerVolumeStatus[] | undefined,
  match?: { name?: string; mountPath?: string; type?: string },
): ContainerVolumeStatus | undefined {
  if (!volumeStatus?.length) return undefined;
  const name = String(match?.name ?? '').trim();
  const mountPath = String(match?.mountPath ?? '').trim();
  const type = match?.type;

  if (name) {
    const byName = volumeStatus.find((item) => String(item.name ?? '').trim() === name);
    if (byName) return byName;
  }
  if (mountPath) {
    const byMountPath = volumeStatus.find((item) => String(item.mountPath ?? '').trim() === mountPath);
    if (byMountPath) return byMountPath;
  }
  if (type) {
    const byType = volumeStatus.find((item) => String(item.type ?? '').toLowerCase() === type.toLowerCase());
    if (byType) return byType;
  }
  return undefined;
}

/** 从 workload.status.volumeStatus 解析单卷 storageClass */
export function resolveStorageClassFromVolumeStatus(
  workload?: Pick<MaasWorkload, 'status'>,
  match?: { name?: string; mountPath?: string; type?: string },
): StorageClassDisplayInfo | undefined {
  const entry = findVolumeStatusEntry(workload?.status?.volumeStatus, match);
  return mapApiStorageClassToDisplayInfo(entry?.storageClass);
}

/** ModelInstance：取 type=Data 的首个卷 storageClass */
export function resolveDataVolumeStorageClassFromStatus(workload?: Pick<MaasWorkload, 'status'>): StorageClassDisplayInfo | undefined {
  const volumeStatus = workload?.status?.volumeStatus;
  if (!volumeStatus?.length) return undefined;
  const dataVolume =
    volumeStatus.find((item) => String(item.type ?? '').toLowerCase() === 'data') ||
    volumeStatus.find((item) => String(item.name ?? '').toLowerCase() === MODEL_DATA_VOLUME_NAME) ||
    volumeStatus[0];
  return mapApiStorageClassToDisplayInfo(dataVolume?.storageClass);
}

/** AppInstance：按 volume.name 索引 volumeStatus */
export function buildVolumeStatusByName(workload?: Pick<MaasWorkload, 'status'>): Map<string, ContainerVolumeStatus> {
  const result = new Map<string, ContainerVolumeStatus>();
  (workload?.status?.volumeStatus || []).forEach((item) => {
    const name = String(item.name ?? '').trim();
    if (name) result.set(name, item);
  });
  return result;
}

/** 模型实例 Data 卷固定名称（与后端约定一致） */
export const MODEL_DATA_VOLUME_NAME = 'model';

/** 应用实例 Data 卷默认名称 */
export const APP_DATA_VOLUME_NAME = 'data';

/** UI 专用：已有 PVC 选中时缓存的容量（Gi），提交前剔除 */
export const DATA_VOLUME_PVC_CAPACITY_FIELD = '_pvcStorageGi';

/** UI 专用：已有 PVC 选中时缓存的展示名称（alias），提交前剔除 */
export const DATA_VOLUME_PVC_DISPLAY_NAME_FIELD = '_pvcDisplayName';

/** 系统卷默认名称 */
export const SYSTEM_VOLUME_NAME = 'image';

/** 与 CRD enum 一致（Emerpheral 为后端 CRD 拼写） */
export const VOLUME_SOURCE_EPHEMERAL = 'Emerpheral';
export const VOLUME_SOURCE_PVC = 'PersistentVolumeClaim';
export const VOLUME_SOURCE_PVC_TEMPLATE = 'PersistentVolumeClaimTemplate';

/** 读取 Data 卷挂载路径 */
export function getVolumeMountPath(volume?: Record<string, unknown>): string | undefined {
  if (!volume) return undefined;
  const mountPath = get(volume, 'source.mountPath') || get(volume, 'source.persistentVolumeClaimInfo.mountPath');
  return mountPath ? String(mountPath) : undefined;
}

/** 读取已有 PVC 名称 */
export function getVolumeClaimName(volume?: Record<string, unknown>): string | undefined {
  if (!volume) return undefined;
  const claim =
    get(volume, 'source.persistentVolumeClaimName') ||
    get(volume, 'source.persistentVolumeClaimInfo.claimName') ||
    get(volume, 'source.claimName');
  return claim ? String(claim) : undefined;
}

/** 读取已有 PVC 展示名称（表单缓存 alias，无则 undefined） */
export function getVolumeClaimDisplayName(volume?: Record<string, unknown>): string | undefined {
  if (!volume) return undefined;
  const displayName = volume[DATA_VOLUME_PVC_DISPLAY_NAME_FIELD];
  return displayName != null && String(displayName).trim() !== '' ? String(displayName) : undefined;
}

/** 读取 Data 卷可用存储容量（Gi）：已有 PVC 缓存、PVC 模板 requests.storage 或顶层 size */
export function getDataVolumeStorageGi(volume?: Record<string, unknown>): number | undefined {
  if (!volume) return undefined;

  const cached = volume[DATA_VOLUME_PVC_CAPACITY_FIELD];
  if (cached !== undefined && cached !== null && cached !== '') {
    const num = Number(cached);
    if (Number.isFinite(num) && num > 0) return num;
  }

  const template = getVolumePvcTemplate(volume);
  const templateRaw = get(template, 'resources.requests.storage') || get(template, 'resources.limits.storage');
  if (templateRaw !== undefined && templateRaw !== null && templateRaw !== '') {
    const size = Number(String(templateRaw).replace(/Gi$/i, ''));
    if (!Number.isNaN(size) && size > 0) return size;
  }

  const topSize = volume.size ?? get(volume, 'size.request') ?? get(volume, 'size.limit');
  if (topSize !== undefined && topSize !== null && topSize !== '') {
    const size = Number(String(topSize).replace(/Gi$/i, ''));
    if (!Number.isNaN(size) && size > 0) return size;
  }

  return undefined;
}

/** 读取 PVC 模板 */
export function getVolumePvcTemplate(volume?: Record<string, unknown>): Record<string, unknown> | undefined {
  if (!volume) return undefined;
  return (
    (get(volume, 'source.persistentVolumeClaimTemplate') as Record<string, unknown> | undefined) ||
    (volume.persistentVolumeClaimTemplate as Record<string, unknown> | undefined)
  );
}

/** 数据盘卡片展示用存储类 alias（优先 storageClassNameOriginal.label） */
export function getVolumeStorageClassDisplay(volume?: Record<string, unknown>): string | undefined {
  const info = getVolumeStorageClassInfo(volume);
  return info?.alias || info?.name;
}

/** 读取创建数据盘时缓存的存储类 provisioner，仅用于表单和预览展示。 */
export function getVolumeStorageClassProvisioner(volume?: Record<string, unknown>): string | undefined {
  return getVolumeStorageClassInfo(volume)?.provisioner;
}

/** 读取创建数据盘时缓存的存储类 name（K8s StorageClass 资源名） */
export function getVolumeStorageClassName(volume?: Record<string, unknown>): string | undefined {
  return getVolumeStorageClassInfo(volume)?.name;
}

/** 从卷列表中查找 Data 卷（兼容 type 大小写、name=model、带 PVC source 的非 System 卷） */
export function findDataVolumeInVolumes(volumes?: Record<string, unknown>[]): Record<string, unknown> | undefined {
  if (!volumes?.length) return undefined;

  const byType = volumes.find((v) => v.type === 'Data' || String(v.type || '').toLowerCase() === 'data');
  if (byType) return byType;

  const byName = volumes.find((v) => {
    if (v.type === 'System') return false;
    const name = String(v.name || '').toLowerCase();
    return name === MODEL_DATA_VOLUME_NAME || name === 'model';
  });
  if (byName) return byName;

  return volumes.find((v) => {
    if (v.type === 'System') return false;
    const sourceType = get(v, 'source.type');
    return (
      sourceType === VOLUME_SOURCE_PVC || sourceType === VOLUME_SOURCE_PVC_TEMPLATE || !!getVolumeClaimName(v) || !!getVolumePvcTemplate(v)
    );
  });
}

/** 从 head/worker 组中查找 Data 卷 */
export function findDataVolumeInGroupedSpec(spec?: {
  headGroupSpec?: { resource?: { storage?: { volume?: Record<string, unknown>[] } } };
  workerGroupSpecs?: { resource?: { storage?: { volume?: Record<string, unknown>[] } } }[];
}): Record<string, unknown> | undefined {
  if (!spec) return undefined;
  const groups = [spec.headGroupSpec, ...(spec.workerGroupSpecs || [])].filter(Boolean);
  for (const group of groups) {
    const found = findDataVolumeInVolumes(get(group, 'resource.storage.volume') as Record<string, unknown>[] | undefined);
    if (found) return found;
  }
  return undefined;
}

/** 从 spec.inference 各拓扑解析模型 Data 卷（不含 kvOffload / gateway） */
export function findModelDataVolumeInInferenceSpec(inference?: MaasInferenceSpec): Record<string, unknown> | undefined {
  if (!inference) return undefined;
  const topologies = [inference.normal, inference.primarySecondary, inference.prefillDecode?.prefill, inference.prefillDecode?.decode];
  for (const topology of topologies) {
    if (!topology) continue;
    const found = findDataVolumeInGroupedSpec({
      headGroupSpec: topology.headGroupSpec,
      workerGroupSpecs: topology.workerGroupSpecs,
    });
    if (found) return found;
  }
  return undefined;
}

/** 从 status.volumeStatus 选取模型 Data 卷条目（优先 name=model） */
export function findDataVolumeStatusEntry(volumeStatus?: ContainerVolumeStatus[]): ContainerVolumeStatus | undefined {
  if (!volumeStatus?.length) return undefined;
  const byModelName = volumeStatus.find(
    (item) => String(item.type ?? '').toLowerCase() === 'data' && String(item.name ?? '').toLowerCase() === MODEL_DATA_VOLUME_NAME,
  );
  if (byModelName) return byModelName;
  return volumeStatus.find((item) => String(item.type ?? '').toLowerCase() === 'data');
}

/** 无 spec 组内卷时，由 volumeStatus 构造表单可读 Data 卷 */
export function buildDataVolumeFromVolumeStatus(entry: ContainerVolumeStatus, mountPath: string): Record<string, unknown> {
  const resolvedMountPath = String(entry.mountPath || mountPath || '/models').trim() || '/models';
  const volumeName = String(entry.name || MODEL_DATA_VOLUME_NAME).trim() || MODEL_DATA_VOLUME_NAME;
  const claimName = String(entry.persistentVolumeClaim?.name ?? '').trim();
  const reclaimPolicy = entry.persistentVolumeClaim?.reclaimPolicy;

  if (claimName) {
    const pvcGi = parseStorageQuantityToGi(entry.size);
    return {
      name: volumeName,
      type: 'Data',
      ...(reclaimPolicy ? { reclaimPolicy } : {}),
      ...(pvcGi !== undefined ? { [DATA_VOLUME_PVC_CAPACITY_FIELD]: pvcGi } : {}),
      source: {
        type: VOLUME_SOURCE_PVC,
        persistentVolumeClaimName: claimName,
        mountPath: resolvedMountPath,
      },
    };
  }

  const storageGi = parseStorageQuantityToGi(entry.size);
  const storageClassName = entry.storageClass?.name;
  return {
    name: volumeName,
    type: 'Data',
    ...(reclaimPolicy ? { reclaimPolicy } : {}),
    source: {
      type: VOLUME_SOURCE_PVC_TEMPLATE,
      mountPath: resolvedMountPath,
      persistentVolumeClaimTemplate: {
        ...(storageClassName ? { storageClassName } : {}),
        accessModes: entry.persistentVolumeClaim?.accessModes?.length ? entry.persistentVolumeClaim.accessModes : ['ReadWriteMany'],
        ...(storageGi !== undefined ? { resources: { requests: { storage: `${storageGi}Gi` } } } : {}),
      },
    },
  };
}

/** 读取系统卷大小 Gi */
export function getSystemVolumeSizeGi(volume?: Record<string, unknown>): number | undefined {
  if (!volume) return undefined;
  const raw = volume.size || get(volume, 'persistentVolumeClaimTemplate.resources.requests.storage');
  const size = Number(String(raw ?? '').replace('Gi', ''));
  return !Number.isNaN(size) && size > 0 ? size : undefined;
}

/** 创建型数据盘是否已填写容量（storageClassName 仅来自 device，可为 undefined） */
export function isCreateDataVolumeConfigured(volume: Record<string, unknown>): boolean {
  const template = getVolumePvcTemplate(volume);
  return !!get(template, 'resources.requests.storage');
}

/** 判断是否为创建 PVC 模板的数据盘 */
export function isCreateDataVolume(volume?: Record<string, unknown>): boolean {
  if (!volume) return false;
  if (volume.type === 'System') return false;
  if (getVolumeClaimName(volume)) return false;
  const sourceType = get(volume, 'source.type');
  if (sourceType === VOLUME_SOURCE_PVC) return false;
  if (sourceType === VOLUME_SOURCE_EPHEMERAL) return false;
  if (sourceType === VOLUME_SOURCE_PVC_TEMPLATE) return true;
  const template = getVolumePvcTemplate(volume);
  if (template) {
    if (get(template, 'resources.requests.storage') || get(template, 'resources.limits.storage')) return true;
    if (get(template, 'storageClassName')) return true;
  }
  if (volume.type === 'Data' && getDataVolumeStorageGi(volume) !== undefined) return true;
  return false;
}

/** 判断是否为挂载已有 PVC 的数据盘 */
export function isExistingDataVolume(volume?: Record<string, unknown>): boolean {
  if (!volume) return false;
  const sourceType = get(volume, 'source.type');
  if (sourceType === VOLUME_SOURCE_PVC) return !!getVolumeClaimName(volume);
  return !!getVolumeClaimName(volume);
}

/** 提交前规范化 System 卷：Emerpheral + volume.size */
export function normalizeSystemVolumeForSubmit(volume: Record<string, unknown>, sizeGi: number, mountPath = '/'): Record<string, unknown> {
  const next = { ...volume };
  next.name = next.name || SYSTEM_VOLUME_NAME;
  next.type = 'System';
  next.size = `${sizeGi}Gi`;
  next.source = {
    type: VOLUME_SOURCE_EPHEMERAL,
    mountPath,
    ...(get(next, 'source') as Record<string, unknown> | undefined),
  };
  set(next, 'source.type', VOLUME_SOURCE_EPHEMERAL);
  set(next, 'source.mountPath', get(next, 'source.mountPath') || mountPath);
  unset(next, 'persistentVolumeClaimTemplate');
  unset(next, 'source.persistentVolumeClaimInfo');
  unset(next, 'source.persistentVolumeClaimName');
  unset(next, 'source.persistentVolumeClaimTemplate');
  return next;
}

/** 提交前规范化 Data 卷，对齐 VolumeSource CRD */
export function normalizeDataVolumeForSubmit(
  volume: Record<string, unknown>,
  mountPath: string,
  options?: { volumeName?: string },
): Record<string, unknown> {
  const next = { ...volume };
  const volumeName = options?.volumeName || String(next.name || MODEL_DATA_VOLUME_NAME);
  next.name = volumeName;
  next.type = 'Data';

  if (isExistingDataVolume(next)) {
    const claimName = getVolumeClaimName(next);
    next.source = {
      type: VOLUME_SOURCE_PVC,
      persistentVolumeClaimName: claimName,
      mountPath: getVolumeMountPath(next) || mountPath,
    };
    unset(next, 'persistentVolumeClaimTemplate');
    unset(next, 'source.persistentVolumeClaimInfo');
    unset(next, 'source.persistentVolumeClaimTemplate');
    unset(next, DATA_VOLUME_PVC_CAPACITY_FIELD);
    unset(next, DATA_VOLUME_PVC_DISPLAY_NAME_FIELD);
    unset(next, 'storageClassNameOriginal');
    return next;
  }

  const template = getVolumePvcTemplate(next);
  next.source = {
    type: VOLUME_SOURCE_PVC_TEMPLATE,
    mountPath: getVolumeMountPath(next) || mountPath,
    persistentVolumeClaimTemplate: template,
  };
  unset(next, 'persistentVolumeClaimTemplate');
  unset(next, 'source.persistentVolumeClaimInfo');
  unset(next, 'source.persistentVolumeClaimName');
  unset(next, DATA_VOLUME_PVC_CAPACITY_FIELD);
  unset(next, DATA_VOLUME_PVC_DISPLAY_NAME_FIELD);
  unset(next, 'storageClassNameOriginal');
  return next;
}

/** 详情/编辑态：将 API 卷结构转为表单可读结构（保留 source 字段，清理废弃嵌套） */
export function normalizeDataVolumeForForm(volume: Record<string, unknown>, fallbackMountPath: string): Record<string, unknown> {
  const next = { ...volume };
  if (!next.type) {
    next.type = 'Data';
  }
  const mountPath = getVolumeMountPath(next) || fallbackMountPath;
  const claimName = getVolumeClaimName(next);
  let template = getVolumePvcTemplate(next);

  // API 可能仅有顶层 size，补全 PVC 模板供表单/卡片识别
  if (!template && !claimName && getDataVolumeStorageGi(next) !== undefined) {
    const storageGi = getDataVolumeStorageGi(next)!;
    template = {
      accessModes: (next.accessModes as string[] | undefined) || ['ReadWriteOnce'],
      storageClassName: get(next, 'storageClassName') as string | undefined,
      resources: { requests: { storage: `${storageGi}Gi` } },
    };
  }

  if (isExistingDataVolume(next) || claimName) {
    next.source = {
      type: VOLUME_SOURCE_PVC,
      persistentVolumeClaimName: claimName,
      mountPath,
    };
  } else if (template) {
    next.source = {
      type: VOLUME_SOURCE_PVC_TEMPLATE,
      mountPath,
      persistentVolumeClaimTemplate: template,
    };
  } else if (get(next, 'source.type') === VOLUME_SOURCE_PVC_TEMPLATE) {
    next.source = {
      ...(get(next, 'source') as Record<string, unknown>),
      type: VOLUME_SOURCE_PVC_TEMPLATE,
      mountPath,
    };
  }

  unset(next, 'persistentVolumeClaimTemplate');
  unset(next, 'source.persistentVolumeClaimInfo');
  return next;
}

/** 详情/编辑态：规范化 System 卷 */
export function normalizeSystemVolumeForForm(volume: Record<string, unknown>): Record<string, unknown> {
  const next = { ...volume };
  const sizeGi = getSystemVolumeSizeGi(next);
  if (sizeGi) {
    next.size = `${sizeGi}Gi`;
  }
  if (!get(next, 'source.type')) {
    next.source = {
      type: VOLUME_SOURCE_EPHEMERAL,
      mountPath: get(next, 'source.mountPath') || '/',
    };
  }
  unset(next, 'persistentVolumeClaimTemplate');
  unset(next, 'source.persistentVolumeClaimInfo');
  return next;
}
